"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Edit2, Plus, Users } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { IngredientList } from "@/lib/types";

interface ListDropdownProps {
  lists: IngredientList[];
  activeList: IngredientList | undefined;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSelectList: (listId: string) => void;
  onEditList: (list: IngredientList) => void;
  onAddList: () => void;
}

export function ListDropdown({
  lists,
  activeList,
  onSelectList,
  onEditList,
  onAddList,
}: ListDropdownProps) {
  const t = useTranslations("lists");
  const [isMounted, setIsMounted] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const sortedLists = useMemo(
    () => [...lists].sort((a, b) => a.name.localeCompare(b.name)),
    [lists]
  );

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1], // iOS-style easing
        }}
      >
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2 max-w-[250px] sm:max-w-[300px]">
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: isMounted ? activeList?.color || "#ff6b6b" : "#ff6b6b",
                }}
              />
              <span className="truncate">
                {isMounted ? activeList?.name || t("selectList") : t("selectList")}
              </span>
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64 mx-4">
            {sortedLists.map((list) => {
              const showUsers = list.isShared || list.hasShares;

              return (
                <DropdownMenuItem
                  key={list.id}
                  className="flex items-center justify-between"
                  onSelect={(e) => {
                    e.preventDefault();
                  }}
                >
                  <button
                    onClick={() => {
                      onSelectList(list.id);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                  >
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: list.color }}
                    />
                    <span className="flex-1 text-left truncate">{list.name}</span>
                    {showUsers && (
                      <span
                        title={
                          list.isShared
                            ? t("sharedBy", { name: list.ownerName || "someone" })
                            : t("sharedWithOthers")
                        }
                      >
                        <Users className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      </span>
                    )}
                    {activeList?.id === list.id && <Check className="w-4 h-4 flex-shrink-0" />}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditList(list);
                      setOpen(false);
                    }}
                    className="p-1 hover:bg-accent hover:text-accent-foreground rounded ml-2 transition-colors cursor-pointer"
                    title={list.isShared ? t("viewSharedList") : t("editList")}
                    aria-label={list.isShared ? t("viewSharedList") : t("editList")}
                  >
                    <Edit2 className="w-4 h-4 text-primary" />
                  </button>
                </DropdownMenuItem>
              );
            })}
            {lists.length > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={onAddList}>
              <Plus className="w-4 h-4 mr-2" />
              <span>{t("addList")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </motion.div>
    </AnimatePresence>
  );
}
